import { fetchWithAuth, type AccessTokenGetter } from "@/lib/utils";
import { invalidateCache, writeCache } from "@/lib/ui-cache";
import { preferencePreview } from "@/lib/ui-cache-shapes";

export interface UnifiedSidebarPinOrder {
  agentIds: string[];
  projectIds: string[];
}

export interface UnifiedPreferencesSnapshot {
  preferences: Record<string, unknown>;
  agentPins: string[];
  sidebarPinOrder: UnifiedSidebarPinOrder;
  updatedAt: string | null;
}

export interface UnifiedPreferencesPatch {
  preferences?: Record<string, unknown>;
  agentPins?: string[];
  sidebarPinOrder?: UnifiedSidebarPinOrder;
}

const UNIFIED_PREFERENCES_URL = "/api/preferences";
const LEGACY_DOCUMENT_URL = "/api/user-preferences";
const LEGACY_AGENT_PINS_URL = "/api/preferences/agent-pins";
const LEGACY_SIDEBAR_PIN_ORDER_URL = "/api/preferences/sidebar-pin-order";
const UNSUPPORTED_STATUSES = new Set([404, 405, 501]);

const readsInFlight = new WeakMap<
  AccessTokenGetter,
  Promise<UnifiedPreferencesSnapshot>
>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function responseError(response: Response): Error {
  return new Error("HTTP " + response.status + ": " + response.statusText);
}

function responseIds(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const item of value) {
    if (typeof item !== "string" || !item || seen.has(item)) return null;
    seen.add(item);
    ids.push(item);
  }
  return ids;
}

function responseOrder(value: unknown): UnifiedSidebarPinOrder | null {
  if (!isRecord(value)) return null;
  const agentIds = responseIds(value.agent_ids);
  const projectIds = responseIds(value.project_ids);
  if (!agentIds || !projectIds) return null;
  return { agentIds, projectIds };
}

function responseDocument(value: unknown): {
  preferences: Record<string, unknown>;
  updatedAt: string | null;
} | null {
  if (!isRecord(value) || !isRecord(value.preferences)) return null;
  return {
    preferences: value.preferences,
    updatedAt: typeof value.updated_at === "string" ? value.updated_at : null,
  };
}

function unifiedResponse(value: unknown): UnifiedPreferencesSnapshot | null {
  if (!isRecord(value)) return null;
  const document = responseDocument(value);
  const agentPins = responseIds(value.agent_pins);
  const sidebarPinOrder = responseOrder(value.sidebar_pin_order);
  if (!document || !agentPins || !sidebarPinOrder) return null;
  return {
    preferences: document.preferences,
    agentPins,
    sidebarPinOrder,
    updatedAt: document.updatedAt,
  };
}

function unsupported(response: Response): boolean {
  return UNSUPPORTED_STATUSES.has(response.status);
}

async function json(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function readLegacyPreferences(
  getToken: AccessTokenGetter,
): Promise<UnifiedPreferencesSnapshot> {
  const responses = await Promise.all([
    fetchWithAuth(LEGACY_DOCUMENT_URL, { headers: {}, cache: "no-store" }, getToken),
    fetchWithAuth(LEGACY_AGENT_PINS_URL, { headers: {}, cache: "no-store" }, getToken),
    fetchWithAuth(LEGACY_SIDEBAR_PIN_ORDER_URL, { headers: {}, cache: "no-store" }, getToken),
  ]);
  if (!responses[0].ok) throw responseError(responses[0]);
  if (!responses[1].ok) throw responseError(responses[1]);
  if (!responses[2].ok && !unsupported(responses[2])) {
    throw responseError(responses[2]);
  }

  const document = responseDocument(await json(responses[0]));
  const pinsPayload = await json(responses[1]);
  const orderPayload = responses[2].ok ? await json(responses[2]) : null;
  const agentPins = isRecord(pinsPayload) ? responseIds(pinsPayload.agent_ids) : null;
  const sidebarPinOrder = responseOrder(orderPayload)
    ?? { agentIds: [], projectIds: [] };
  if (!document || !agentPins) {
    throw new Error("HTTP 502: Bad Gateway");
  }
  return {
    preferences: document.preferences,
    agentPins,
    sidebarPinOrder,
    updatedAt: document.updatedAt,
  };
}

async function readPreferences(
  getToken: AccessTokenGetter,
): Promise<UnifiedPreferencesSnapshot> {
  const response = await fetchWithAuth(
    UNIFIED_PREFERENCES_URL,
    { headers: {}, cache: "no-store" },
    getToken,
  );
  if (unsupported(response)) return readLegacyPreferences(getToken);
  if (!response.ok) throw responseError(response);
  const snapshot = unifiedResponse(await json(response));
  return snapshot ?? readLegacyPreferences(getToken);
}

function toWirePatch(patch: UnifiedPreferencesPatch): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (patch.preferences) body.preferences = patch.preferences;
  if (patch.agentPins) body.agent_pins = patch.agentPins;
  if (patch.sidebarPinOrder) {
    body.sidebar_pin_order = {
      agent_ids: patch.sidebarPinOrder.agentIds,
      project_ids: patch.sidebarPinOrder.projectIds,
    };
  }
  return body;
}

async function writeLegacyPreferences(
  getToken: AccessTokenGetter,
  patch: UnifiedPreferencesPatch,
): Promise<UnifiedPreferencesSnapshot> {
  if (patch.preferences) {
    const response = await fetchWithAuth(
      LEGACY_DOCUMENT_URL,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch.preferences),
      },
      getToken,
    );
    if (!response.ok) throw responseError(response);
  }

  if (patch.agentPins) {
    const current = await readLegacyPreferences(getToken);
    for (const agentId of current.agentPins) {
      if (patch.agentPins.includes(agentId)) continue;
      const response = await fetchWithAuth(
        LEGACY_AGENT_PINS_URL,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ agent_id: agentId }),
        },
        getToken,
      );
      if (!response.ok) throw responseError(response);
    }
    for (const agentId of patch.agentPins) {
      if (current.agentPins.includes(agentId)) continue;
      const response = await fetchWithAuth(
        LEGACY_AGENT_PINS_URL,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ agent_id: agentId }),
        },
        getToken,
      );
      if (!response.ok) throw responseError(response);
    }
  }

  if (patch.sidebarPinOrder) {
    const response = await fetchWithAuth(
      LEGACY_SIDEBAR_PIN_ORDER_URL,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agent_ids: patch.sidebarPinOrder.agentIds,
          project_ids: patch.sidebarPinOrder.projectIds,
        }),
      },
      getToken,
    );
    if (!response.ok) throw responseError(response);
  }
  return readLegacyPreferences(getToken);
}

async function writePreferences(
  getToken: AccessTokenGetter,
  patch: UnifiedPreferencesPatch,
): Promise<UnifiedPreferencesSnapshot> {
  const response = await fetchWithAuth(
    UNIFIED_PREFERENCES_URL,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(toWirePatch(patch)),
    },
    getToken,
  );
  if (unsupported(response)) return writeLegacyPreferences(getToken, patch);
  if (!response.ok) throw responseError(response);
  const snapshot = unifiedResponse(await json(response));
  return snapshot ?? writeLegacyPreferences(getToken, patch);
}

/**
 * Read the complete preference envelope. Concurrent shell consumers sharing
 * the same token getter share this in-flight request, so the initial render
 * does not split preferences, pins, and sidebar order into three requests.
 */
export function readUnifiedPreferences(
  getToken: AccessTokenGetter,
): Promise<UnifiedPreferencesSnapshot> {
  const existing = readsInFlight.get(getToken);
  if (existing) return existing;
  const request = readPreferences(getToken);
  void request.then((snapshot) => writeCache("displayPrefs", "display", "settings", preferencePreview(snapshot))).catch(() => undefined);
  readsInFlight.set(getToken, request);
  void request.then(
    () => {
      if (readsInFlight.get(getToken) === request) {
        readsInFlight.delete(getToken);
      }
    },
    () => {
      if (readsInFlight.get(getToken) === request) {
        readsInFlight.delete(getToken);
      }
    },
  );
  return request;
}

/**
 * Patch one or more supported preference parts. The facade handles old
 * server fallback; an independently older bridge falls back client-side.
 */
export function patchUnifiedPreferences(
  getToken: AccessTokenGetter,
  patch: UnifiedPreferencesPatch,
): Promise<UnifiedPreferencesSnapshot> {
  return writePreferences(getToken, patch).then((snapshot) => {
    void invalidateCache("displayPrefs");
    return snapshot;
  });
}
